/**
 * The grouped operator dropdown, derived from a surface's declared families.
 *
 * The surface declares `readonly GroupableFamily[]` once; both the dropdown and
 * the plugin's parse gate read that one declaration, so the two cannot disagree
 * about which operators the surface offers.
 */

import { isOneOfOperator, isOperatorInFamilies, operatorsInFamily, timestampVariantOf } from './operators';
import type { OperatorFamily } from './operators';

export type OperatorOption = { value: string; label: string; isDisabled?: boolean };
export type OperatorGroup = { label: string; options: OperatorOption[] };

/**
 * Which families each dropdown group shows, in display order — both the order
 * of the groups and the order of families within a group. This is the one
 * place the mapping lives; a surface declares families and the groups follow,
 * so the dropdown can't drift from the plugin's claim.
 */
const GROUP_FAMILIES = [
  { label: 'String or Number', families: ['equality', 'inequality', 'oneOf', 'existence'] },
  { label: 'String', families: ['string'] },
  { label: 'Email', families: ['email'] },
  { label: 'Unix Timestamp', families: ['dateExistence', 'dateInequality', 'extraDateInequality'] },
] as const satisfies readonly { label: string; families: readonly OperatorFamily[] }[];

/**
 * The families a dropdown can actually show — those `GROUP_FAMILIES` places in
 * a group. Deliberately narrower than `OperatorFamily`: `array` has no ported
 * sub-editor. Surfaces declare `readonly GroupableFamily[]`, so declaring a
 * family with nowhere to render is a compile error rather than an option that
 * silently vanishes from the dropdown while the plugin's `parse` still claims
 * it.
 */
export type GroupableFamily = (typeof GROUP_FAMILIES)[number]['families'][number];

/**
 * The per-surface field rules the shared vocabulary cannot know on its own.
 * Every property is optional and every default is permissive, so a surface with
 * no domain constraints declares families and nothing else.
 */
export type AttributeFieldPolicy = {
  /**
   * Attribute names the `one_of` operators are unavailable for. People passes
   * the two the backend rejects at query time; a surface without that
   * restriction omits it and every field keeps the operator.
   */
  oneOfDisallowedFields?: ReadonlySet<string>;
  /**
   * The single attribute name the email operators apply to, or undefined to let
   * them apply everywhere. People pins this to 'email' — the only field the
   * backend's email_valid index covers.
   *
   * Compared untrimmed on purpose: the raw attribute name is the identity, so a
   * padded '  email  ' is a different attribute and the operator can't apply.
   */
  emailField?: string;
  /**
   * The largest `one_of` list the surface accepts, or undefined for no cap.
   */
  oneOfMaxValues?: number;
};

export const EMPTY_ATTRIBUTE_FIELD_POLICY: AttributeFieldPolicy = {};

function optionsForFamily(family: GroupableFamily, field: string, policy: AttributeFieldPolicy): OperatorOption[] {
  if (family === 'oneOf') {
    const isDisabled = policy.oneOfDisallowedFields?.has(field.trim()) ?? false;
    return operatorsInFamily('oneOf').map((operator) => ({ ...operator, isDisabled }));
  }
  if (family === 'email') {
    // The email operators stay visible but are only usable on the surface's
    // email field, when it declares one.
    const isDisabled = policy.emailField != null && field !== policy.emailField;
    return operatorsInFamily('email').map((operator) => ({ ...operator, isDisabled }));
  }
  if (family === 'dateInequality') {
    // Only the relative pair is pickable: the absolute variants are reached
    // through the row's "a relative date / a specific date" select, which
    // rewrites the operator between the paired forms.
    return operatorsInFamily(family).filter((operator) => timestampVariantOf(operator.value) === 'relative');
  }
  return [...operatorsInFamily(family)];
}

/**
 * Derives the grouped operator dropdown from a surface's declared families.
 * Groups with no declared families are dropped rather than rendered empty, so
 * the returned list is exactly what the surface offers.
 */
export function groupsForFamilies(
  declared: readonly GroupableFamily[],
  field: string,
  policy: AttributeFieldPolicy = EMPTY_ATTRIBUTE_FIELD_POLICY,
  allowOneOfOperators = false,
): OperatorGroup[] {
  const allowed = new Set<GroupableFamily>(declared);
  if (!allowOneOfOperators) {
    allowed.delete('oneOf');
  }
  const groups: OperatorGroup[] = [];
  for (const { label, families } of GROUP_FAMILIES) {
    const options = families
      .filter((family) => allowed.has(family))
      .flatMap((family) => optionsForFamily(family, field, policy));
    if (options.length > 0) {
      groups.push({ label, options });
    }
  }
  return groups;
}

/**
 * An operator a surface hasn't declared is declined rather than parsed into a
 * row whose dropdown can't offer it: dryad keeps the condition as a read-only
 * error node that re-serializes its raw wire unchanged. Gated on the same
 * declared families the dropdown derives from (via groupsForFamilies), so the
 * two readers cannot disagree.
 */
export function isSupportedOperator(
  operator: string,
  families: readonly GroupableFamily[],
  allowOneOfOperators: boolean,
): boolean {
  if (!allowOneOfOperators && isOneOfOperator(operator)) {
    return false;
  }
  return isOperatorInFamilies(operator, new Set(families));
}
